import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { COLORS } from '../data/constants';

const formatVideoTime = (seconds) => {
  if (seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const PriceChart = ({
  data,
  marketQuestion,
  userMoments = [],
  currentVideoTimestamp = null,
  startTimestamp = null,
  onSeekToTime,
}) => {
  if (!data || data.length === 0) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Market Probability</h3>
        <div style={styles.emptyState}>
          No probability data available for the selected time range.
        </div>
      </div>
    );
  }

  // Transform data for recharts
  const chartData = data.map(point => {
    const videoTime = startTimestamp ? point.timestamp - startTimestamp : 0;
    return {
      time: new Date(point.timestamp * 1000).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      videoTime,
      videoTimeFormatted: formatVideoTime(videoTime),
      probability: parseFloat((point.probability * 100).toFixed(2)),
      timestamp: point.timestamp,
    };
  });

  // Compute Y domain snapped to 5% increments, showing all tick marks
  const probValues = chartData.map(d => d.probability);
  const rawMin = Math.min(...probValues);
  const rawMax = Math.max(...probValues);
  const yMin = Math.max(0, Math.floor(rawMin / 5) * 5 - 5);
  const yMax = Math.min(100, Math.ceil(rawMax / 5) * 5 + 5);

  // Generate explicit ticks at every 5% within domain
  const yTicks = [];
  for (let v = yMin; v <= yMax; v += 5) {
    yTicks.push(v);
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div style={styles.tooltip}>
          <div style={styles.tooltipProb}>{d.probability}%</div>
          <div style={styles.tooltipTime}>{d.videoTimeFormatted}</div>
        </div>
      );
    }
    return null;
  };

  const handleChartClick = (data) => {
    if (data?.activePayload?.[0]) {
      const clicked = data.activePayload[0].payload;
      if (onSeekToTime && clicked.videoTime !== undefined) {
        onSeekToTime(clicked.videoTime);
      }
    }
  };

  const playheadTimestamp =
    startTimestamp && currentVideoTimestamp != null
      ? startTimestamp + currentVideoTimestamp
      : null;

  // Filter user moments to visible range
  const dataStart = chartData[0]?.timestamp;
  const dataEnd = chartData[chartData.length - 1]?.timestamp;
  const visibleMoments = userMoments.filter(
    (m) => m.timestamp >= dataStart && m.timestamp <= dataEnd
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerRow}>
        <h3 style={styles.title}>Market Probability</h3>
        <p style={styles.subtitle}>{marketQuestion}</p>
      </div>

      {/* Chart */}
      <div style={styles.chartArea}>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 16, left: -8, bottom: 4 }}
            onClick={handleChartClick}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid
              horizontal={true}
              vertical={false}
              strokeDasharray="4 4"
              stroke="rgba(255,255,255,0.08)"
            />

            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 11 }}
              interval="preserveStartEnd"
              minTickGap={50}
            />
            <YAxis
              domain={[yMin, yMax]}
              ticks={yTicks}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 11 }}
              tickFormatter={(v) => `${v}%`}
              width={44}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: 'rgba(255,255,255,0.15)', strokeWidth: 1 }}
            />

            {/* User moment markers */}
            {visibleMoments.map((m) => (
              <ReferenceLine
                key={`um-${m.id}`}
                x={new Date(m.timestamp * 1000).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                stroke="rgba(255,255,255,0.35)"
                strokeWidth={1}
                strokeDasharray="4 4"
              />
            ))}

            {/* Playhead */}
            {playheadTimestamp && (
              <ReferenceLine
                x={new Date(playheadTimestamp * 1000).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                stroke={COLORS.PRIMARY_BLUE}
                strokeWidth={2}
                label={{
                  value: '\u25B6',
                  position: 'top',
                  fill: COLORS.PRIMARY_BLUE,
                  fontSize: 12,
                }}
              />
            )}

            <Line
              type="monotone"
              dataKey="probability"
              stroke={COLORS.PRIMARY_BLUE}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: COLORS.PRIMARY_BLUE, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <StatItem label="Points" value={data.length} />
        <StatItem
          label="Avg"
          value={`${(data.reduce((s, d) => s + d.probability, 0) / data.length * 100).toFixed(1)}%`}
        />
        <StatItem
          label="Range"
          value={`${(Math.min(...data.map(d => d.probability)) * 100).toFixed(0)}–${(Math.max(...data.map(d => d.probability)) * 100).toFixed(0)}%`}
        />
      </div>
    </div>
  );
};

const StatItem = ({ label, value }) => (
  <div style={styles.statItem}>
    <span style={styles.statLabel}>{label}</span>
    <span style={styles.statValue}>{value}</span>
  </div>
);

const styles = {
  container: {
    backgroundColor: '#141722',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  headerRow: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#6b7280',
    margin: '4px 0 0 0',
    lineHeight: '1.4',
  },
  chartArea: {
    marginBottom: '12px',
  },
  tooltip: {
    background: '#1e2235',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    padding: '8px 12px',
  },
  tooltipProb: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#fff',
    lineHeight: 1,
  },
  tooltipTime: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
  },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  statValue: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#d1d5db',
  },
  emptyState: {
    padding: '48px 0',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '14px',
  },
};

export default PriceChart;
